import { useRef, useState } from "react";
import { Search, Upload, Loader2, Copy, Trash2, Eye, FileIcon, Save } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "@/lib/error-message";
import { QueryError } from "@/components/Common/QueryError";
import type { Media } from "@endeks/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useMediaList, useUploadMedia, useDeleteMedia, useUpdateMedia } from "./queries";
import { showDeleteConfirm } from "../../components/Common/DeleteConfirm";

const SENTINEL_ALL = "__all__";

const TYPE_FILTERS = [
  { label: "Tümü", value: SENTINEL_ALL },
  { label: "Resimler", value: "image/" },
  { label: "Videolar", value: "video/" },
  { label: "Dokümanlar", value: "application/" },
] as const;

// Sabit medya kategorileri (backend folders.ts ile aynı)
const FOLDERS = [
  { label: "Markalar", value: "brands" },
  { label: "Blog", value: "blog" },
  { label: "İçerik", value: "content" },
  { label: "Sosyal", value: "social" },
  { label: "Genel", value: "general" },
] as const;

interface DetailDialogState {
  item: Media;
  alt: string;
}

export default function MediaBrowser() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [mimeTypeFilter, setMimeTypeFilter] = useState<string>(SENTINEL_ALL);
  const [folderFilter, setFolderFilter] = useState<string>(SENTINEL_ALL);
  const [uploadFolder, setUploadFolder] = useState<string>("general");
  const [detail, setDetail] = useState<DetailDialogState | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeMime = mimeTypeFilter === SENTINEL_ALL ? undefined : mimeTypeFilter;
  const activeFolder = folderFilter === SENTINEL_ALL ? undefined : folderFilter;

  const {
    data: mediaData,
    isLoading,
    isError,
    error,
    refetch,
  } = useMediaList({
    page,
    limit: 24,
    search: search || undefined,
    mimeType: activeMime,
    folder: activeFolder,
  });

  const uploadMutation = useUploadMedia();
  const deleteMutation = useDeleteMedia();
  const updateMutation = useUpdateMedia();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      try {
        await uploadMutation.mutateAsync({ file, folder: uploadFolder });
        toast.success(`${file.name} yüklendi`);
      } catch (err) {
        const reason = errorMessage(err, "");
        toast.error(reason ? `${file.name} yüklenemedi: ${reason}` : `${file.name} yüklenemedi`);
      }
    }
    e.target.value = "";
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url).then(
      () => toast.success("URL kopyalandı"),
      () => toast.error("URL kopyalanamadı"),
    );
  };

  const handleSaveAlt = () => {
    if (!detail) return;
    updateMutation.mutate(
      { id: detail.item.id, alt: detail.alt },
      {
        onSuccess: () => {
          toast.success("Alt metin kaydedildi");
          setDetail(null);
        },
        onError: (err: unknown) => toast.error(errorMessage(err, "Kayıt başarısız")),
      },
    );
  };

  const handleDelete = (item: Media) => {
    showDeleteConfirm({
      onConfirm: () =>
        deleteMutation.mutate(item.id, {
          onSuccess: () => {
            toast.success("Dosya silindi");
            if (detail?.item.id === item.id) setDetail(null);
          },
          onError: (err: unknown) => toast.error(errorMessage(err, "Dosya silinemedi")),
        }),
      title: "Dosya Sil",
      description: `"${item.originalName}" dosyasını silmek istediğinize emin misiniz?`,
    });
  };

  const items: Media[] = mediaData?.items ?? [];
  const totalPages: number = mediaData?.totalPages ?? 1;
  const total: number = mediaData?.total ?? 0;

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Medya</h1>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            multiple
            onChange={handleFileChange}
          />
          <Select value={uploadFolder} onValueChange={setUploadFolder}>
            <SelectTrigger className="w-36" title="Yükleme kategorisi">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FOLDERS.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploadMutation.isPending}>
            {uploadMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Dosya Yükle
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Dosya ara..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-8"
          />
        </div>
        <Select
          value={mimeTypeFilter}
          onValueChange={(v) => {
            setMimeTypeFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TYPE_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={folderFilter}
          onValueChange={(v) => {
            setFolderFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-40" title="Kategori">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SENTINEL_ALL}>Tüm kategoriler</SelectItem>
            {FOLDERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <Skeleton className="h-32 w-full rounded-md" />
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          ))}
        </div>
      ) : isError && !mediaData ? (
        <QueryError error={error} onRetry={() => void refetch()} />
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground text-sm gap-2">
          <FileIcon className="h-10 w-10 opacity-40" />
          <p>Henüz medya dosyası yok. Dosya yükleyerek başlayın.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {items.map((item) => (
              <div
                key={item.id}
                className="group relative rounded-md border border-border overflow-hidden bg-muted/20 cursor-pointer hover:border-primary transition-colors"
                onClick={() => setDetail({ item, alt: item.alt ?? "" })}
              >
                {/* Thumbnail */}
                <div className="h-32 flex items-center justify-center bg-muted/10">
                  {item.mimeType?.startsWith("image/") ? (
                    <img
                      src={item.url}
                      alt={item.alt || item.originalName}
                      className="max-w-full max-h-32 object-contain"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-muted-foreground">
                      <FileIcon className="h-8 w-8 opacity-50" />
                      <span className="text-[10px] uppercase font-medium">
                        {item.mimeType?.split("/")[1] || "FILE"}
                      </span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-1.5">
                  <p className="text-[11px] truncate leading-tight font-medium">
                    {item.originalName}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {(item.size / 1024).toFixed(0)} KB
                  </p>
                </div>

                {/* Hover overlay actions */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors opacity-0 group-hover:opacity-100 flex items-start justify-end p-1 gap-1">
                  <button
                    className="rounded bg-background/80 p-0.5 hover:bg-background"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(item.url, "_blank");
                    }}
                    title="Görüntüle"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                  <button
                    className="rounded bg-background/80 p-0.5 hover:bg-destructive hover:text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item);
                    }}
                    title="Sil"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Önceki
              </Button>
              <span className="text-sm text-muted-foreground">
                {page} / {totalPages} ({total} dosya)
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Sonraki
              </Button>
            </div>
          )}
        </>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={!!detail}
        onOpenChange={(open) => {
          if (!open) setDetail(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="truncate text-sm font-medium">
              {detail?.item.originalName}
            </DialogTitle>
          </DialogHeader>

          {detail && (
            <div className="space-y-4">
              {/* Preview */}
              <div className="flex items-center justify-center rounded-md border bg-muted/20 h-56">
                {detail.item.mimeType?.startsWith("image/") ? (
                  <img
                    src={detail.item.url}
                    alt={detail.item.alt || detail.item.originalName}
                    className="max-w-full max-h-56 object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <FileIcon className="h-12 w-12 opacity-40" />
                    <span className="text-xs uppercase">
                      {detail.item.mimeType?.split("/")[1] || "FILE"}
                    </span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="text-xs text-muted-foreground space-y-0.5">
                <p>Boyut: {(detail.item.size / 1024).toFixed(0)} KB</p>
                <p className="truncate">URL: {detail.item.url}</p>
              </div>

              {/* Alt text */}
              <div className="space-y-1.5">
                <Label htmlFor="media-alt">Alt metin</Label>
                <Input
                  id="media-alt"
                  value={detail.alt}
                  onChange={(e) => setDetail((d) => (d ? { ...d, alt: e.target.value } : d))}
                  placeholder="Görseli tanımlayan metin..."
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => detail && handleCopyUrl(detail.item.url)}
            >
              <Copy className="h-4 w-4" />
              URL Kopyala
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => detail && handleDelete(detail.item)}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
              Sil
            </Button>
            <Button size="sm" onClick={handleSaveAlt} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
